import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import { useI18n } from '../lib/use-i18n.js';
import { logger } from '../lib/logger.js';
import { callRetentionPulse, type RetentionPulseOutput } from '../lib/callables/ai.js';
import { money, parseRateLimitError } from '@kilat/shared';

/**
 * Retention pulse — AI-tools Slice 2 (final). A one-glance "N regulars have gone
 * quiet, RM X of business at risk — here's what to do" nudge that drives the
 * operator into the win-back segment tool they already have.
 *
 * The load-bearing value is the AGGREGATE rule (`retentionPulse` callable):
 * `lapsedCount` + `atRiskSpendSen` over the tenant customer index, using the
 * SAME lapse threshold as the win-back segment criterion. The AI narrative is a
 * THIN optional layer that no-ops to `null` without a Groq key — the card still
 * shows the numbers + CTA, never a dead spinner.
 *
 * No-dead-ends: every state is explicit — loading, error (retry), zero-lapsed
 * (a positive "everyone's active" state, NOT a blank), and the has-lapsed state
 * (numbers + optional narrative + an always-present "Create win-back campaign"
 * CTA that deep-links into the segment builder prefilled with the same window).
 *
 * In-flow section (no floating chrome) — mounts at the top of /marketing.
 */

const WINDOW_DAYS = 30;

type State =
  | { kind: 'loading' }
  | { kind: 'error'; message: string; retryAfterSec?: number }
  | { kind: 'ready'; data: RetentionPulseOutput };

export function RetentionPulseCard({ tenantId }: { readonly tenantId: string }) {
  const t = useI18n();
  const [state, setState] = useState<State>({ kind: 'loading' });

  const load = useCallback(async () => {
    setState({ kind: 'loading' });
    try {
      const data = await callRetentionPulse({ tenantId, windowDays: WINDOW_DAYS });
      setState({ kind: 'ready', data });
    } catch (err) {
      const rl = parseRateLimitError(err);
      if (rl) {
        setState({ kind: 'error', message: rl.message, retryAfterSec: rl.retryAfterSec });
        return;
      }
      logger.error('retentionPulse failed', { tenantId, error: String(err) });
      setState({ kind: 'error', message: t.t('dashboard.marketing.pulse.errorTitle') });
    }
  }, [tenantId, t]);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <section style={cardStyle} aria-live="polite">
      <header style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <span aria-hidden style={badgeStyle}>
          {'✦'}
        </span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <h2 style={{ margin: 0, fontSize: 'var(--text-body)', fontWeight: 600 }}>
            {t.t('dashboard.marketing.pulse.heading')}
          </h2>
          <p
            style={{
              margin: '2px 0 0 0',
              fontSize: 'var(--text-meta)',
              color: 'var(--color-on-surface-muted)',
            }}
          >
            {t.t('dashboard.marketing.pulse.tagline')}
          </p>
        </div>
      </header>

      <div style={{ marginTop: 'var(--space-4)' }}>
        {state.kind === 'loading' ? (
          <p style={mutedNoteStyle}>{t.t('dashboard.marketing.pulse.loading')}</p>
        ) : state.kind === 'error' ? (
          <div role="alert" style={{ display: 'grid', gap: 'var(--space-2)' }}>
            <p style={{ margin: 0, color: 'var(--color-accent)', fontSize: 'var(--text-meta)' }}>
              {state.message}
              {state.retryAfterSec ? (
                <span
                  style={{ display: 'block', marginTop: 4, color: 'var(--color-on-surface-muted)' }}
                >
                  {t.t('dashboard.marketing.pulse.retryIn', { count: state.retryAfterSec })}
                </span>
              ) : null}
            </p>
            <button type="button" onClick={() => void load()} style={ghostButtonStyle}>
              {t.t('dashboard.marketing.pulse.retry')}
            </button>
          </div>
        ) : state.data.lapsedCount === 0 ? (
          <ZeroState />
        ) : (
          <LapsedState data={state.data} />
        )}
      </div>
    </section>
  );
}

function ZeroState() {
  const t = useI18n();
  return (
    <div
      style={{
        display: 'grid',
        gap: 'var(--space-1)',
        padding: 'var(--space-3) var(--space-4)',
        borderRadius: 'var(--radius-md)',
        background: 'rgba(13,122,79,0.06)',
        border: '1px solid rgba(13,122,79,0.18)',
      }}
    >
      <p style={{ margin: 0, fontWeight: 600, color: 'var(--color-primary)' }}>
        {t.t('dashboard.marketing.pulse.zeroTitle')}
      </p>
      <p
        style={{ margin: 0, fontSize: 'var(--text-meta)', color: 'var(--color-on-surface-muted)' }}
      >
        {t.t('dashboard.marketing.pulse.zeroBody', { days: WINDOW_DAYS })}
      </p>
    </div>
  );
}

function LapsedState({ data }: { readonly data: RetentionPulseOutput }) {
  const t = useI18n();
  const headline =
    data.lapsedCount === 1
      ? t.t('dashboard.marketing.pulse.lapsedOne')
      : t.t('dashboard.marketing.pulse.lapsedOther', { count: data.lapsedCount });

  return (
    <div style={{ display: 'grid', gap: 'var(--space-3)' }}>
      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 'var(--space-4)',
          alignItems: 'flex-end',
        }}
      >
        <div style={{ minWidth: 0 }}>
          <p style={{ margin: 0, fontSize: 'var(--text-h1)', fontWeight: 700, lineHeight: 1.1 }}>
            {headline}
          </p>
          <p
            style={{
              margin: 'var(--space-1) 0 0 0',
              fontSize: 'var(--text-meta)',
              color: 'var(--color-on-surface-muted)',
            }}
          >
            {t.t('dashboard.marketing.pulse.window', { days: data.windowDays })}
          </p>
        </div>
        <div style={{ minWidth: 0 }}>
          <p
            style={{
              margin: 0,
              fontSize: 'var(--text-body)',
              fontWeight: 600,
              color: 'var(--color-accent)',
              fontVariantNumeric: 'tabular-nums',
            }}
          >
            {t.t('dashboard.marketing.pulse.atRisk', { amount: money.format(data.atRiskSpendSen) })}
          </p>
          {data.truncated ? (
            <p
              style={{ margin: '2px 0 0 0', fontSize: 11, color: 'var(--color-on-surface-muted)' }}
            >
              {t.t('dashboard.marketing.pulse.truncatedNote')}
            </p>
          ) : null}
        </div>
      </div>

      {data.narrative ? (
        <div
          style={{
            display: 'flex',
            gap: 'var(--space-2)',
            padding: 'var(--space-3) var(--space-4)',
            borderRadius: 'var(--radius-md)',
            background: 'rgba(13,122,79,0.06)',
            border: '1px solid rgba(13,122,79,0.18)',
          }}
        >
          <span
            aria-hidden
            style={{
              fontSize: 9,
              padding: '2px 6px',
              borderRadius: 999,
              background: 'rgba(13,122,79,0.12)',
              color: 'var(--color-primary)',
              fontWeight: 700,
              letterSpacing: 0.3,
              height: 'fit-content',
            }}
          >
            {t.t('dashboard.marketing.pulse.aiBadge')}
          </span>
          <p
            style={{
              margin: 0,
              fontSize: 'var(--text-meta)',
              color: 'var(--color-on-surface)',
              lineHeight: 1.5,
              whiteSpace: 'pre-line',
            }}
          >
            {data.narrative}
          </p>
        </div>
      ) : null}

      <div style={{ display: 'flex', gap: 'var(--space-2)', flexWrap: 'wrap' }}>
        <Link
          to={`/marketing/segments?inactiveDays=${data.windowDays}`}
          style={{ ...primaryButtonStyle, textDecoration: 'none', display: 'inline-flex' }}
        >
          {t.t('dashboard.marketing.pulse.cta')}
        </Link>
      </div>
    </div>
  );
}

// ── styles (mirror the marketing AI panel language) ─────────────────────

const cardStyle: React.CSSProperties = {
  background: 'var(--color-surface)',
  borderRadius: 'var(--radius-lg)',
  padding: 'var(--space-5)',
  boxShadow: 'var(--shadow-1)',
  border: '1px solid rgba(13,122,79,0.16)',
};

const badgeStyle: React.CSSProperties = {
  display: 'inline-grid',
  placeItems: 'center',
  width: 26,
  height: 26,
  borderRadius: 8,
  background: 'linear-gradient(135deg, #0d7a4f 0%, #0f8659 100%)',
  color: '#ffffff',
  fontSize: 12,
};

const mutedNoteStyle: React.CSSProperties = {
  margin: 0,
  fontSize: 'var(--text-meta)',
  color: 'var(--color-on-surface-muted)',
};

const primaryButtonStyle: React.CSSProperties = {
  background: 'var(--color-primary)',
  color: 'var(--color-primary-fg)',
  padding: '12px 18px',
  border: 'none',
  borderRadius: 'var(--radius-md)',
  fontWeight: 500,
  fontSize: 'var(--text-body)',
  cursor: 'pointer',
  boxShadow: 'var(--shadow-2)',
};

const ghostButtonStyle: React.CSSProperties = {
  background: 'transparent',
  color: 'var(--color-on-surface-muted)',
  padding: '10px 16px',
  border: '1px solid var(--color-border-strong)',
  borderRadius: 'var(--radius-md)',
  fontSize: 'var(--text-meta)',
  fontWeight: 500,
  cursor: 'pointer',
  width: 'fit-content',
};
